/**
 * 备赛进度时间轴（可视化①）：把「四段进度条」升级为「走到哪一步」的阶段轨道。
 * - 段宽 = 固定权重（15/35/35/15），段内填充 = 该阶段完成度（数据全部来自后端 progress 接口，前端不换算）；
 * - 当前阶段 = 第一个未完成的阶段（全部 100% → 已完赛）；
 * - 下方叠加赛事最近两个未过期里程碑倒计时（daysLeft 由后端实时计算，前端不做日期运算）。
 */
import { useEventTimelineQuery } from '../hooks/queries.js';
import type { ProjectProgressDto } from '../api/types.js';
import { PHASE_LABELS, PHASE_ORDER } from '../lib/labels.js';
import { CountdownBadge } from './CountdownBadge.js';

/** 权重 → 轨道宽度占比（与后端 PHASE_WEIGHTS 一致的纯展示映射）。 */
const WIDTH_BY_PHASE: Record<number, number> = { 1: 15, 2: 35, 3: 35, 4: 15 };

type StageState = 'done' | 'current' | 'pending';

function stageState(pct: number, isCurrent: boolean): StageState {
  if (pct >= 100) return 'done';
  return isCurrent ? 'current' : 'pending';
}

export function PrepTimeline({ progress, eventCode }: { progress: ProjectProgressDto; eventCode: string | null }) {
  const timelineQuery = useEventTimelineQuery(eventCode);
  const allDone = progress.phases.every((phase) => phase.pct >= 100);
  const currentPhase = allDone ? null : progress.phases.find((phase) => phase.pct < 100)?.phase ?? null;
  const labelOf = (phase: number): string => PHASE_LABELS[phase as 1 | 2 | 3 | 4] ?? `阶段 ${phase}`;

  const upcoming = (timelineQuery.data?.milestones ?? [])
    .filter((milestone) => milestone.daysLeft !== null && milestone.daysLeft >= 0)
    .sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0))
    .slice(0, 2);

  return (
    <div className="prep-timeline stack-sm" aria-label="备赛进度时间轴">
      <div className="prep-track">
        {PHASE_ORDER.map((phase) => {
          const stat = progress.phases.find((item) => item.phase === phase);
          const pct = stat?.pct ?? 0;
          const state = stageState(pct, phase === currentPhase);
          const width = WIDTH_BY_PHASE[phase] ?? 25;
          return (
            <div
              key={phase}
              className={`prep-segment prep-segment-${state}`}
              style={{ flexBasis: `${width}%` }}
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${labelOf(phase)}完成度 ${pct}%`}
            >
              <div className="prep-segment-fill" style={{ width: `${pct}%` }} />
              <div className="prep-segment-body">
                <span className="prep-segment-name">
                  {state === 'done' ? '✓ ' : ''}
                  {labelOf(phase)}
                </span>
                <span className="prep-segment-num">
                  {stat?.done ?? 0}/{stat?.total ?? 0} · {pct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="row-tight prep-track-footer">
        <span className="muted small">
          {allDone
            ? '四个阶段全部完成，可以安心路演了。'
            : currentPhase !== null
              ? `当前阶段：${labelOf(currentPhase)}（权重 ${WIDTH_BY_PHASE[currentPhase] ?? 25}%）`
              : '阶段待办生成后，这里会显示当前进度。'}
        </span>
        <span className="prep-overall">
          整体进度 <strong>{progress.overall}%</strong>
        </span>
      </div>

      {upcoming.length > 0 ? (
        <div className="row-tight prep-milestones" aria-label="临近赛事节点">
          {upcoming.map((milestone) => (
            <span key={milestone.id} className="prep-milestone">
              {milestone.label}
              <CountdownBadge daysLeft={milestone.daysLeft} />
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
